import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNotification } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { BLOG_LIMITS, isValidSlug, suggestSlug } from "@hedefuni/domain/blog";

import { api, ApiError } from "../api";
import { BlogEditor } from "../components/blog-editor";
import { uploadBlogImage } from "../components/blog-image";
import type {
  BlogAuthor,
  BlogCategory,
  BlogFaqItem,
  BlogPostDetail,
  BlogPostStatus,
  SeoCheck,
} from "../types";

type Values = {
  title: string;
  slug: string;
  seoTitle: string;
  description: string;
  summary: string;
  faq: BlogFaqItem[];
  coverAlt: string;
  noindex: boolean;
  status: BlogPostStatus;
  publishedAt: Dayjs | null;
  authorId: string;
  categoryId: string | null;
};

const SITE_HOST = "hedefuni.com";

function counter(length: number, ideal: number) {
  return (
    <Typography.Text type={length > ideal ? "danger" : "secondary"}>
      {length} / {ideal}
    </Typography.Text>
  );
}

/**
 * /blog/new and /blog/:id (permission `blog.manage`; product decision 2026-10-10). The post's
 * text on the left; publishing, the cover, the search fields, a Google preview and the SEO
 * checklist on the right. A published post's old address keeps leading to the new one.
 */
export function BlogPostEditPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Values>();
  // Edited values; until the first edit, the loaded post's.
  const [editedBody, setBody] = useState<string | null>(null);
  const [editedCover, setCover] = useState<{ key: string; url: string } | null | undefined>(
    undefined,
  );
  const [checks, setChecks] = useState<SeoCheck[] | null>(null);
  const [saving, setSaving] = useState(false);
  const slugTouched = useRef(!isNew);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { data: post, error } = useQuery({
    queryKey: ["office", "blog", "post", id],
    queryFn: () => api<BlogPostDetail>(`/api/office/blog/posts/${encodeURIComponent(id ?? "")}`),
    enabled: !isNew,
  });
  const { data: authors } = useQuery({
    queryKey: ["office", "blog", "authors"],
    queryFn: () => api<BlogAuthor[]>("/api/office/blog/authors"),
  });
  const { data: categories } = useQuery({
    queryKey: ["office", "blog", "categories"],
    queryFn: () => api<BlogCategory[]>("/api/office/blog/categories"),
  });

  useEffect(() => {
    if (!post) return;
    form.setFieldsValue({
      title: post.title,
      slug: post.slug,
      seoTitle: post.seoTitle ?? "",
      description: post.description,
      summary: post.summary,
      faq: post.faq,
      coverAlt: post.coverAlt ?? "",
      noindex: post.noindex,
      status: post.status,
      publishedAt: post.publishedAt ? dayjs(post.publishedAt) : null,
      authorId: post.authorId,
      categoryId: post.categoryId,
    });
    refreshChecks(post.bodyHtml);
    // Runs once per loaded post.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  useEffect(() => () => clearTimeout(timer.current), []);
  const body = editedBody ?? post?.bodyHtml ?? "";
  const cover =
    editedCover !== undefined
      ? editedCover
      : post?.coverKey && post.coverUrl
        ? { key: post.coverKey, url: post.coverUrl }
        : null;
  const watched = Form.useWatch([], form) as Partial<Values> | undefined;

  function payload(values: Values, bodyHtml: string, coverValue: typeof cover) {
    return {
      title: values.title ?? "",
      slug: values.slug ?? "",
      seoTitle: values.seoTitle ?? "",
      description: values.description ?? "",
      summary: values.summary ?? "",
      bodyHtml,
      faq: (values.faq ?? []).map((item) => ({
        question: item?.question ?? "",
        answer: item?.answer ?? "",
      })),
      coverKey: coverValue?.key ?? null,
      coverAlt: values.coverAlt ?? "",
      categoryId: values.categoryId ?? null,
    };
  }

  function refreshChecks(bodyHtml = body, coverValue = cover) {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        setChecks(
          await api<SeoCheck[]>("/api/office/blog/check", {
            method: "POST",
            body: payload(form.getFieldsValue(true), bodyHtml, coverValue),
          }),
        );
      } catch {
        // The checklist is a hint; saving reports real problems.
      }
    }, 600);
  }

  function onValuesChange(changed: Partial<Values>) {
    if ("title" in changed && !slugTouched.current) {
      form.setFieldValue("slug", suggestSlug(changed.title ?? ""));
    }
    if ("slug" in changed) slugTouched.current = true;
    refreshChecks();
  }

  async function onCover(file: File | undefined) {
    if (!file) return;
    try {
      const uploaded = await uploadBlogImage(file);
      setCover(uploaded);
      refreshChecks(body, uploaded);
    } catch (cause) {
      open?.({ type: "error", message: cause instanceof Error ? cause.message : "Yüklenemedi." });
    }
  }

  async function save(values: Values) {
    setSaving(true);
    const bodyPayload = {
      ...payload(values, body, cover),
      noindex: values.noindex ?? false,
      status: values.status,
      publishedAt: values.publishedAt ? values.publishedAt.toISOString() : null,
      authorId: values.authorId,
    };
    try {
      if (isNew) {
        const created = await api<{ id: string }>("/api/office/blog/posts", {
          method: "POST",
          body: bodyPayload,
        });
        open?.({ type: "success", message: "Yazı kaydedildi." });
        navigate(`/blog/${created.id}`, { replace: true });
      } else {
        await api(`/api/office/blog/posts/${encodeURIComponent(id)}`, {
          method: "PUT",
          body: bodyPayload,
        });
        open?.({ type: "success", message: "Yazı kaydedildi." });
      }
      await queryClient.invalidateQueries({ queryKey: ["office", "blog"] });
    } catch (cause) {
      open?.({
        type: "error",
        message: cause instanceof ApiError ? cause.message : "Kaydedilemedi. Tekrar dene.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    try {
      await api(`/api/office/blog/posts/${encodeURIComponent(id ?? "")}`, { method: "DELETE" });
      await queryClient.invalidateQueries({ queryKey: ["office", "blog"] });
      navigate("/blog");
    } catch (cause) {
      open?.({ type: "error", message: cause instanceof ApiError ? cause.message : "Silinemedi." });
    }
  }

  if (error) {
    return (
      <Alert type="error" message={error instanceof ApiError ? error.message : "Yüklenemedi."} />
    );
  }
  if (!isNew && !post) return <Card loading />;

  const slug = watched?.slug ?? "";
  const shownTitle = (watched?.seoTitle || watched?.title || "").trim();
  const description = watched?.description ?? "";
  const isLive =
    post?.status === "PUBLISHED" &&
    post.publishedAt !== null &&
    dayjs(post.publishedAt).isBefore(dayjs());
  const slugChanged = Boolean(post && isLive && slug && slug !== post.slug);
  const passed = checks?.filter((check) => check.ok).length ?? 0;

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={save}
      onValuesChange={onValuesChange}
      initialValues={{ status: "DRAFT", noindex: false, faq: [], publishedAt: null }}
    >
      <Space style={{ justifyContent: "space-between", width: "100%", marginBottom: 16 }} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {isNew ? "Yeni yazı" : "Yazıyı düzenle"}
        </Typography.Title>
        <Space wrap>
          <Button onClick={() => navigate("/blog")}>Yazılara dön</Button>
          {isLive && post ? (
            <Button href={post.url} target="_blank" rel="noopener noreferrer">
              Sitede aç
            </Button>
          ) : null}
          {!isNew && post?.status === "DRAFT" ? (
            <Popconfirm
              title="Taslak silinsin mi?"
              okText="Sil"
              cancelText="Vazgeç"
              okButtonProps={{ danger: true }}
              onConfirm={remove}
            >
              <Button danger icon={<DeleteOutlined />}>
                Sil
              </Button>
            </Popconfirm>
          ) : null}
          <Button type="primary" htmlType="submit" loading={saving}>
            Kaydet
          </Button>
        </Space>
      </Space>

      <Row gutter={24}>
        <Col xs={24} xl={16}>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Form.Item
              name="title"
              label="Başlık (sayfadaki ana başlık)"
              rules={[{ required: true, message: "Başlığı yaz." }]}
            >
              <Input maxLength={BLOG_LIMITS.titleMax} showCount size="large" />
            </Form.Item>
            <Form.Item
              name="slug"
              label="Adres (slug)"
              extra={`${SITE_HOST}/blog/${slug || "…"}`}
              rules={[
                { required: true, message: "Adresi yaz." },
                {
                  validator: (_, value: string) =>
                    !value || isValidSlug(value)
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            "Sadece küçük harf, rakam ve tek tire (ör. tyt-matematik-netleri).",
                          ),
                        ),
                },
              ]}
            >
              <Input
                maxLength={BLOG_LIMITS.slugMax}
                addonAfter={
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      form.setFieldValue("slug", suggestSlug(form.getFieldValue("title") ?? ""));
                      slugTouched.current = true;
                      refreshChecks();
                    }}
                  >
                    Başlıktan üret
                  </Button>
                }
              />
            </Form.Item>
            {slugChanged ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Kaydedince eski adres (/blog/${post?.slug}) yeni adrese kalıcı olarak yönlendirilir.`}
              />
            ) : null}
            {post && post.oldSlugs.length > 0 ? (
              <Typography.Paragraph type="secondary">
                Yeni adrese yönlenen eski adresler:{" "}
                {post.oldSlugs.map((old) => `/blog/${old}`).join(", ")}
              </Typography.Paragraph>
            ) : null}
            <Form.Item
              name="summary"
              label="Özet (yazının başında, sorunun kısa cevabı)"
              tooltip="İlk cümlede doğrudan cevap ver; Google ve yapay zekâ cevapları bu bölümü kullanır."
              rules={[{ required: true, message: "Özeti yaz." }]}
            >
              <Input.TextArea
                maxLength={BLOG_LIMITS.summaryMax}
                showCount
                autoSize={{ minRows: 3 }}
              />
            </Form.Item>
          </Card>

          <Card size="small" title="Yazı" style={{ marginBottom: 16 }}>
            <BlogEditor
              value={body}
              onChange={(html) => {
                setBody(html);
                refreshChecks(html);
              }}
            />
          </Card>

          <Card size="small" title="Sık sorulan sorular" style={{ marginBottom: 16 }}>
            <Typography.Paragraph type="secondary">
              Yazının sonunda gösterilir ve yapılandırılmış veri (FAQPage) olarak eklenir.
            </Typography.Paragraph>
            <Form.List name="faq">
              {(fields, { add, remove: removeField }) => (
                <>
                  {fields.map((field, index) => (
                    <Card
                      key={field.key}
                      size="small"
                      style={{ marginBottom: 12 }}
                      title={`Soru ${index + 1}`}
                      extra={
                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          aria-label={`Soru ${index + 1} kaldır`}
                          onClick={() => {
                            removeField(field.name);
                            refreshChecks();
                          }}
                        />
                      }
                    >
                      <Form.Item name={[field.name, "question"]} label="Soru">
                        <Input maxLength={300} />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, "answer"]}
                        label="Cevap"
                        style={{ marginBottom: 0 }}
                      >
                        <Input.TextArea maxLength={3000} autoSize={{ minRows: 2 }} />
                      </Form.Item>
                    </Card>
                  ))}
                  {fields.length < BLOG_LIMITS.faqMax ? (
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => add({ question: "", answer: "" })}
                    >
                      Soru ekle
                    </Button>
                  ) : null}
                </>
              )}
            </Form.List>
          </Card>
        </Col>

        <Col xs={24} xl={8}>
          <Card size="small" title="Yayın" style={{ marginBottom: 16 }}>
            <Form.Item name="status" label="Durum">
              <Select
                showSearch
                optionFilterProp="label"
                options={[
                  { value: "DRAFT", label: "Taslak" },
                  { value: "PUBLISHED", label: "Yayında" },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="publishedAt"
              label="Yayın tarihi"
              extra="Boş bırakırsan yayına aldığın an. İleri bir tarih yazıyı o zamana planlar."
            >
              <DatePicker
                showTime={{ format: "HH:mm" }}
                format="DD.MM.YYYY HH:mm"
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              name="authorId"
              label="Yazar"
              rules={[{ required: true, message: "Yazar seç." }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={
                  authors?.length === 0 ? "Önce Yazarlar sayfasından yazar ekle" : "Yazar seç"
                }
                options={(authors ?? []).map((author) => ({
                  value: author.id,
                  label: author.name,
                }))}
              />
            </Form.Item>
            <Form.Item name="categoryId" label="Kategori">
              <Select
                showSearch
                optionFilterProp="label"
                allowClear
                placeholder="Kategori seç"
                options={(categories ?? []).map((category) => ({
                  value: category.id,
                  label: category.name,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="noindex"
              label="Arama motorlarında gösterme"
              valuePropName="checked"
              tooltip="Açıksa sayfa Google'da listelenmez ve site haritasına girmez."
              style={{ marginBottom: 0 }}
            >
              <Switch />
            </Form.Item>
          </Card>

          <Card size="small" title="Kapak görseli" style={{ marginBottom: 16 }}>
            {cover ? (
              <img
                src={cover.url}
                alt=""
                style={{ width: "100%", borderRadius: 8, marginBottom: 12 }}
              />
            ) : null}
            <Space style={{ marginBottom: 12 }}>
              <Button
                icon={<UploadOutlined />}
                onClick={() => document.getElementById("blog-cover")?.click()}
              >
                {cover ? "Değiştir" : "Yükle"}
              </Button>
              {cover ? (
                <Button
                  onClick={() => {
                    setCover(null);
                    refreshChecks(body, null);
                  }}
                >
                  Kaldır
                </Button>
              ) : null}
            </Space>
            <input
              id="blog-cover"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={(event) => onCover(event.target.files?.[0])}
            />
            <Form.Item
              name="coverAlt"
              label="Görselin açıklaması (alt metin)"
              style={{ marginBottom: 0 }}
              extra="1200×630 piksel önerilir; paylaşımlarda da bu görsel çıkar."
            >
              <Input maxLength={BLOG_LIMITS.coverAltMax} />
            </Form.Item>
          </Card>

          <Card size="small" title="Arama sonucu" style={{ marginBottom: 16 }}>
            <Form.Item
              name="seoTitle"
              label="Arama başlığı (boşsa ana başlık)"
              extra={counter(shownTitle.length, BLOG_LIMITS.seoTitleIdeal)}
            >
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item
              name="description"
              label="Meta açıklama"
              rules={[{ required: true, message: "Meta açıklamayı yaz." }]}
              extra={counter(description.length, BLOG_LIMITS.descriptionIdeal)}
            >
              <Input.TextArea maxLength={BLOG_LIMITS.descriptionMax} autoSize={{ minRows: 3 }} />
            </Form.Item>
            <div
              aria-label="Google önizlemesi"
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                padding: 12,
                fontFamily: "Arial, sans-serif",
              }}
            >
              <div style={{ color: "#202124", fontSize: 12 }}>
                {SITE_HOST} › blog › {slug || "…"}
              </div>
              <div style={{ color: "#1a0dab", fontSize: 18, lineHeight: 1.3, margin: "4px 0" }}>
                {shownTitle.length > BLOG_LIMITS.seoTitleIdeal
                  ? `${shownTitle.slice(0, BLOG_LIMITS.seoTitleIdeal)}…`
                  : shownTitle || "Başlık"}
              </div>
              <div style={{ color: "#4d5156", fontSize: 13, lineHeight: 1.5 }}>
                {description.length > BLOG_LIMITS.descriptionIdeal
                  ? `${description.slice(0, BLOG_LIMITS.descriptionIdeal)}…`
                  : description || "Meta açıklama"}
              </div>
            </div>
          </Card>

          <Card
            size="small"
            title="SEO kontrol listesi"
            extra={checks ? `${passed} / ${checks.length}` : null}
          >
            {checks ? (
              <Space direction="vertical" size={8}>
                {checks.map((check) => (
                  <Space key={check.id} align="start">
                    {check.ok ? (
                      <CheckCircleFilled style={{ color: "#52c41a" }} aria-label="Tamam" />
                    ) : (
                      <CloseCircleFilled style={{ color: "#ff4d4f" }} aria-label="Eksik" />
                    )}
                    <Typography.Text>{check.message}</Typography.Text>
                  </Space>
                ))}
              </Space>
            ) : (
              <Typography.Text type="secondary">Yazdıkça burada kontrol edilir.</Typography.Text>
            )}
          </Card>
        </Col>
      </Row>
    </Form>
  );
}
